import React, { useEffect } from 'react';
import { Form, Input, Modal, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { TelegramInboxSettingsEntity, normalizeTelegramInboxSettings } from '../../../domain/entities/TelegramMessenger';

interface InboxNotificationsModalProps {
  open: boolean;
  settings?: TelegramInboxSettingsEntity;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (settings: TelegramInboxSettingsEntity) => Promise<void>;
}

interface InboxNotificationsFormValues {
  enabled: boolean;
  chatId?: string;
}

const InboxNotificationsModal: React.FC<InboxNotificationsModalProps> = ({ open, settings, isSaving, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<InboxNotificationsFormValues>();

  useEffect(() => {
    if (open) form.setFieldsValue({ enabled: settings?.enabled ?? false, chatId: settings?.chatId ?? '' });
  }, [open, settings, form]);

  const validateChatId = async (_: unknown, value?: string) => {
    try {
      normalizeTelegramInboxSettings({ enabled: form.getFieldValue('enabled'), chatId: value });
    } catch (e) {
      throw new Error(t(e instanceof Error ? e.message : 'unknown-error', { ns: 'errors' }));
    }
  };

  return (
    <Modal
      open={open}
      title={t('messenger-inbox-notifications', { ns: 'titles' })}
      okText={t('save', { ns: 'buttons' })}
      cancelText={t('cancel', { ns: 'buttons' })}
      confirmLoading={isSaving}
      onCancel={onCancel}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">{t('messenger-inbox-notifications-hint', { ns: 'standard' })}</Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) => onSubmit({ enabled: values.enabled, chatId: values.chatId })}
      >
        <Form.Item name="enabled" label={t('messenger-inbox-notifications-enabled', { ns: 'labels' })} valuePropName="checked">
          <Switch onChange={() => form.validateFields(['chatId']).catch(() => undefined)} />
        </Form.Item>
        <Form.Item name="chatId" label={t('messenger-inbox-chat-id', { ns: 'labels' })} rules={[{ validator: validateChatId }]}>
          <Input placeholder="123456789" inputMode="numeric" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InboxNotificationsModal;
